// =================================================================================
// File: src/components/Modals/MudoCanvaModal.jsx
// Description: Mudo × Canva integration modal with embedded design studio
// =================================================================================

import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Canva template categories
const CATEGORY_TEMPLATES = {
  workshops: "https://www.canva.com/design/DAGWKqL5XYo/view?embed", // Workshop poster template
  products: "https://www.canva.com/design/DAGWKqL6ABC/view?embed", // Product card template
  socials: "https://www.canva.com/design/DAGWKqL7DEF/view?embed", // Social media post template
  branding: "https://www.canva.com/design/DAGWKqL8GHI/view?embed", // Studio branding template
};

// For now, use Canva's main editor URL as fallback
const CANVA_EDITOR_URL = "https://www.canva.com/create";

const CATEGORY_LABELS = {
  workshops: "Workshop Posters",
  products: "Product Cards",
  socials: "Social Posts",
  branding: "Studio Branding",
};

/**
 * MudoCanvaModal - Full-screen Canva design embed
 * 
 * Features:
 * - Full-screen modal with glass morphism styling
 * - Category selector for different template types
 * - Embedded Canva editor via SDK
 * - Save to Mudo placeholder (future StorageContext integration)
 * - ESC key and backdrop click to close
 */
export default function MudoCanvaModal({ isOpen, onClose, onSaveToMudo }) {
  const [category, setCategory] = useState("workshops");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);

  // Open Canva in new tab immediately when modal opens
  useEffect(() => {
    if (isOpen) {
      // Open Canva in a new tab
      window.open(CANVA_EDITOR_URL, "_blank", "noopener,noreferrer");
      
      // Close modal after a short delay (user opened Canva)
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  // Click outside dropdown to close
  useEffect(() => {
    if (!showCategoryDropdown) return;

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCategoryDropdown]);

  const handleSaveToMudo = useCallback(() => {
    if (onSaveToMudo) {
      onSaveToMudo({
        source: "manual-trigger",
        category,
        timestamp: new Date().toISOString(),
      });
    } else {
      alert("Save functionality coming soon!\n\nYour designs will be automatically saved to your Mudo workspace.");
    }
  }, [onSaveToMudo, category]);

  const handleOpenCanva = useCallback(() => {
    window.open(CANVA_EDITOR_URL, "_blank", "noopener,noreferrer");
  }, []);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
          background: "rgba(0, 0, 0, 0.75)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
        onClick={handleBackdropClick}
      >
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          style={{
            width: "100%",
            height: "100%",
            maxWidth: "1600px",
            maxHeight: "95vh",
            background: "rgba(18, 18, 18, 0.95)",
            backdropFilter: "blur(30px)",
            WebkitBackdropFilter: "blur(30px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "24px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 24px 64px rgba(0, 0, 0, 0.6)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: "20px 24px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
              background: "rgba(255, 255, 255, 0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            {/* Left: Title & Icon */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <img
                src="/canva-icon.svg"
                alt="Canva"
                style={{ width: "32px", height: "32px" }}
              />
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "20px",
                      fontWeight: "700",
                      color: "#fff",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Mudo × Canva Studio
                  </h2>
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: "6px",
                      background: "rgba(147, 51, 234, 0.2)",
                      border: "1px solid rgba(147, 51, 234, 0.3)",
                      color: "#c084fc",
                      fontSize: "11px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Beta
                  </span>
                </div>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "13px",
                    color: "rgba(255, 255, 255, 0.6)",
                  }}
                >
                  Create stunning designs for your studio
                </p>
              </div>
            </div>

            {/* Center: Category Selector */}
            <div style={{ position: "relative" }} ref={dropdownRef}>
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.06)",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.06)";
                }}
              >
                {CATEGORY_LABELS[category]}
                <ChevronDown size={16} />
              </button>

              {/* Dropdown */}
              {showCategoryDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    left: 0,
                    minWidth: "200px",
                    background: "rgba(25, 25, 25, 0.98)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.5)",
                    zIndex: 10000,
                  }}
                >
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setCategory(key);
                        setShowCategoryDropdown(false);
                      }}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        background: key === category ? "rgba(147, 51, 234, 0.15)" : "transparent",
                        border: "none",
                        color: key === category ? "#c084fc" : "#fff",
                        fontSize: "14px",
                        fontWeight: key === category ? "600" : "500",
                        textAlign: "left",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        if (key !== category) {
                          e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (key !== category) {
                          e.currentTarget.style.background = "transparent";
                        }
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            {/* Right: Actions */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <button
                onClick={() => {
                  alert(
                    "📥 How to Save to Mudo:\n\n" +
                    "1. In Canva, click Share → Download\n" +
                    "2. Choose format (PNG/JPG/PDF)\n" +
                    "3. Download to your computer\n" +
                    "4. Go to Templates in Mudo\n" +
                    "5. Upload your downloaded design\n\n" +
                    "💡 Tip: Your designs will be organized by category!"
                  );
                }}
                style={{
                  padding: "10px 20px",
                  borderRadius: "12px",
                  background: "rgba(34, 197, 94, 0.15)",
                  border: "1px solid rgba(34, 197, 94, 0.3)",
                  color: "#4ade80",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(34, 197, 94, 0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(34, 197, 94, 0.15)";
                }}
              >
                📥 How to Save
              </button>

              <button
                onClick={onClose}
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.06)",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.06)";
                }}
                title="Close (ESC)"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Body - Info Card */}
          <div
            style={{
              flex: 1,
              position: "relative",
              overflow: "auto",
              background: "rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            <div
              style={{
                maxWidth: "600px",
                background: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "24px",
                padding: "48px",
                textAlign: "center",
              }}
            >
              <img
                src="/canva-icon.svg"
                alt="Canva"
                style={{ width: "80px", height: "80px", marginBottom: "24px" }}
              />
              
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#fff",
                }}
              >
                Design with Canva
              </h3>
              
              <p
                style={{
                  margin: "0 0 32px 0",
                  fontSize: "16px",
                  color: "rgba(255, 255, 255, 0.7)",
                  lineHeight: "1.6",
                }}
              >
                Canva has been opened in a new browser tab. Create your designs and follow the steps below to add them to Mudo.
              </p>

              {/* Step-by-step instructions */}
              <div
                style={{
                  textAlign: "left",
                  marginBottom: "32px",
                  padding: "24px",
                  borderRadius: "16px",
                  background: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 16px 0",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#fff",
                  }}
                >
                  📋 How to save your Canva design to Mudo:
                </h4>
                <ol
                  style={{
                    margin: 0,
                    padding: "0 0 0 20px",
                    fontSize: "14px",
                    color: "rgba(255, 255, 255, 0.7)",
                    lineHeight: "1.8",
                  }}
                >
                  <li style={{ marginBottom: "8px" }}>Create or edit your design in Canva</li>
                  <li style={{ marginBottom: "8px" }}>Click <strong style={{ color: "#c084fc" }}>Share</strong> → <strong style={{ color: "#c084fc" }}>Download</strong> in Canva</li>
                  <li style={{ marginBottom: "8px" }}>Choose your preferred format (PNG, JPG, or PDF)</li>
                  <li style={{ marginBottom: "8px" }}>Download the file to your computer</li>
                  <li>Upload it to your Mudo workspace using the Gallery or Storage</li>
                </ol>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  marginBottom: "24px",
                }}
              >
                <button
                  onClick={handleOpenCanva}
                  style={{
                    padding: "14px 28px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)",
                    border: "none",
                    color: "#fff",
                    fontSize: "16px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    boxShadow: "0 4px 12px rgba(147, 51, 234, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(147, 51, 234, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(147, 51, 234, 0.3)";
                  }}
                >
                  🎨 Open Canva
                </button>

                <button
                  onClick={() => {
                    onClose();
                    // Navigate to gallery after a short delay
                    setTimeout(() => {
                      window.location.href = "/dashboard/gallery";
                    }, 300);
                  }}
                  style={{
                    padding: "12px 28px",
                    borderRadius: "12px",
                    background: "rgba(34, 197, 94, 0.15)",
                    border: "1px solid rgba(34, 197, 94, 0.3)",
                    color: "#4ade80",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(34, 197, 94, 0.25)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(34, 197, 94, 0.15)";
                  }}
                >
                  📤 Go to Templates Page
                </button>

                <button
                  onClick={onClose}
                  style={{
                    padding: "10px 28px",
                    borderRadius: "12px",
                    background: "rgba(255, 255, 255, 0.06)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    color: "#fff",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.06)";
                  }}
                >
                  Close
                </button>
              </div>

              <div
                style={{
                  padding: "20px",
                  borderRadius: "12px",
                  background: "rgba(147, 51, 234, 0.1)",
                  border: "1px solid rgba(147, 51, 234, 0.2)",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#c084fc",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Template Categories
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    justifyContent: "center",
                  }}
                >
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <span
                      key={key}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "8px",
                        background: key === category 
                          ? "rgba(147, 51, 234, 0.3)" 
                          : "rgba(255, 255, 255, 0.05)",
                        color: key === category ? "#c084fc" : "rgba(255, 255, 255, 0.7)",
                        fontSize: "13px",
                        fontWeight: "500",
                      }}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid rgba(255, 255, 255, 0.08)",
              background: "rgba(255, 255, 255, 0.02)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "12px",
                color: "rgba(255, 255, 255, 0.5)",
              }}
            >
              💡 Create stunning designs for workshops, products, and social media posts.
            </p>
            
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <a
                href="https://canva.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: "12px",
                  color: "rgba(255, 255, 255, 0.6)",
                  textDecoration: "none",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "rgba(255, 255, 255, 0.9)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "rgba(255, 255, 255, 0.6)";
                }}
              >
                Powered by Canva
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}
